import React, { useEffect, useState } from 'react';
import { BaseCard } from './BaseCard';
import { PrimaryButton } from '../Buttons/PrimaryButton';
import { SecondaryButton } from '../Buttons/SecondaryButton';
import { useBoundStore } from '../../Store/store';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';

const timeToMinutes = (timeStr) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
};

export const BranchPercentageCard = ({ branchId }) => {
    const { t } = useTranslation();

    const TYPE_OPTIONS = [
        { value: 'weekdays-7-19', label: t('surcharges.weekdays_7_19') },
        { value: 'weekdays-19-20', label: t('surcharges.weekdays_19_20') },
        { value: 'weekdays-saturdays-after-20', label: t('surcharges.weekdays_sat_20_7') },
        { value: 'saturdays-7-20', label: t('surcharges.saturdays_7_20') },
        { value: 'sundays', label: t('surcharges.sundays') },
        { value: 'custom', label: t('surcharges.dynamic') }
    ];

    const DAY_OPTIONS = [
        { value: 1, label: t('days.mon') },
        { value: 2, label: t('days.tue') },
        { value: 3, label: t('days.wed') },
        { value: 4, label: t('days.thu') },
        { value: 5, label: t('days.fri') },
        { value: 6, label: t('days.sat') },
        { value: 7, label: t('days.sun') }
    ];

    const DEFAULT_ENTRIES = [
        { label: t('surcharges.weekdays_7_19_short'), start_time: '07:00', end_time: '19:00', percentage_value: 100, percentage_type: 'weekdays-7-19', days: [1, 2, 3, 4, 5] },
        { label: t('surcharges.weekdays_19_20_short'), start_time: '19:00', end_time: '20:00', percentage_value: 120, percentage_type: 'weekdays-19-20', days: [1, 2, 3, 4, 5] },
        { label: t('surcharges.weekdays_sat_after_20'), start_time: '20:00', end_time: '24:00', percentage_value: 135, percentage_type: 'weekdays-saturdays-after-20', days: [1, 2, 3, 4, 5, 6] },
        { label: t('surcharges.weekdays_sat_before_7'), start_time: '00:00', end_time: '07:00', percentage_value: 135, percentage_type: 'weekdays-saturdays-after-20', days: [1, 2, 3, 4, 5, 6] },
        { label: t('surcharges.saturdays_7_20_short'), start_time: '07:00', end_time: '20:00', percentage_value: 126, percentage_type: 'saturdays-7-20', days: [6] },
        { label: t('surcharges.sundays'), start_time: '00:00', end_time: '24:00', percentage_value: 156, percentage_type: 'sundays', days: [7] },
    ];

    const getBranchPercentages = useBoundStore((state) => state.getBranchPercentages);
    const updateBranchPercentages = useBoundStore((state) => state.updateBranchPercentages);
    const branchPercentages = useBoundStore((state) => state.branchPercentages);

    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [entries, setEntries] = useState([]);
    const [validationErrors, setValidationErrors] = useState([]);
    const [holidayPercentage, setHolidayPercentage] = useState(156);
    const [interruptedShiftPercentage, setInterruptedShiftPercentage] = useState(150);

    useEffect(() => {
        const fetchPercentages = async () => {
            setIsLoading(true);
            await getBranchPercentages(branchId);
            setIsLoading(false);
        };

        if (branchId) {
            fetchPercentages();
        }
    }, [branchId, getBranchPercentages]);

    useEffect(() => {
        if (branchPercentages && branchPercentages.length > 0) {
            const loadedEntries = [];
            let foundHoliday = false;
            let foundInterrupted = false;

            branchPercentages.forEach(p => {
                if (p.percentage_type === 'holidays') {
                    setHolidayPercentage(p.percentage_value || 156);
                    foundHoliday = true;
                } else if (p.percentage_type === 'interrupted-shifts') {
                    setInterruptedShiftPercentage(p.percentage_value || 150);
                    foundInterrupted = true;
                } else {
                    loadedEntries.push({
                        id: p.id || Date.now() + Math.random(),
                        label: p.label || '',
                        start_time: p.start_time || '00:00',
                        end_time: p.end_time === '00:00:00' ? '24:00' : (p.end_time || '24:00'),
                        percentage_value: p.percentage_value || 100,
                        percentage_type: p.percentage_type || 'custom',
                        days: p.days || []
                    });
                }
            });

            if (!foundHoliday) setHolidayPercentage(156);
            if (!foundInterrupted) setInterruptedShiftPercentage(150);

            setEntries(loadedEntries);
        } else {
            setEntries(DEFAULT_ENTRIES.map(e => ({
                ...e,
                id: Date.now() + Math.random()
            })));
        }
    }, [branchPercentages]);

    const addEntry = () => {
        setEntries([...entries, {
            id: Date.now(),
            label: '',
            start_time: '00:00',
            end_time: '24:00',
            percentage_value: 100,
            percentage_type: 'custom',
            days: []
        }]);
    };

    const removeEntry = (id) => {
        setEntries(entries.filter(e => e.id !== id));
    };

    const updateEntry = (id, field, value) => {
        setEntries(entries.map(e => {
            if (e.id === id) {
                const updates = { [field]: value };
                if (['label', 'percentage_value', 'start_time', 'end_time'].includes(field)) {
                    updates.percentage_type = 'custom';
                }
                return { ...e, ...updates };
            }
            return e;
        }));
    };

    const toggleDay = (id, day) => {
        setEntries(entries.map(e => {
            if (e.id === id) {
                const days = e.days || [];
                if (days.includes(day)) {
                    return { ...e, days: days.filter(d => d !== day) };
                } else {
                    return { ...e, days: [...days, day].sort() };
                }
            }
            return e;
        }));
    };

    const validateEntries = () => {
        const errors = [];

        entries.forEach((entry, index) => {
            const rowNumber = index + 1;

            if (!entry.label || entry.label.trim() === '') {
                errors.push(t('surcharges.errors.label_required', {row: rowNumber}));
            }

            if (!entry.start_time) {
                errors.push(t('surcharges.errors.start_required', {row: rowNumber}));
            }

            if (!entry.end_time) {
                errors.push(t('surcharges.errors.end_required', {row: rowNumber}));
            }

            if (!entry.percentage_value || entry.percentage_value <= 0) {
                errors.push(t('surcharges.errors.percentage_positive', {row: rowNumber}));
            }

            if (!entry.days || entry.days.length === 0) {
                errors.push(t('surcharges.errors.select_day', {row: rowNumber}));
            }
        });

        if (errors.length > 0) {
            setValidationErrors(errors);
            return false;
        }

        for (let day = 1; day <= 7; day++) {
            const dayEntries = entries.filter(e => e.days && e.days.includes(day));

            if (dayEntries.length === 0) {
                errors.push(t('surcharges.errors.no_surcharges_day', {day: DAY_OPTIONS.find(d => d.value === day)?.label}));
                continue;
            }

            const intervals = [];
            dayEntries.forEach(entry => {
                let start = timeToMinutes(entry.start_time);
                let end = timeToMinutes(entry.end_time);

                if (entry.end_time === '24:00') {
                    end = 1440;
                }

                if (end < start) {
                    intervals.push({ start: start, end: 1440, entryId: entry.id });
                    intervals.push({ start: 0, end: end, entryId: entry.id });
                } else {
                    intervals.push({ start, end, entryId: entry.id });
                }
            });

            intervals.sort((a, b) => a.start - b.start);

            for (let i = 0; i < intervals.length - 1; i++) {
                if (intervals[i].end > intervals[i + 1].start) {
                    errors.push(t('surcharges.errors.overlap', {day: DAY_OPTIONS.find(d => d.value === day)?.label}));
                    break;
                }
            }

            if (intervals.length === 0) {
                errors.push(t('surcharges.errors.no_surcharges_set', {day: DAY_OPTIONS.find(d => d.value === day)?.label}));
                continue;
            }

            let currentEnd = 0;

            for (const interval of intervals) {
                if (interval.start > currentEnd) {
                    errors.push(t('surcharges.errors.gap_found', {day: DAY_OPTIONS.find(d => d.value === day)?.label, from: `${Math.floor(currentEnd/60)}:${(currentEnd%60).toString().padStart(2, '0')}`, to: `${Math.floor(interval.start/60)}:${(interval.start%60).toString().padStart(2, '0')}`}));
                    break;
                }
                currentEnd = Math.max(currentEnd, interval.end);
            }

            if (currentEnd < 1440 && errors.filter(e => e.includes(`Dag ${DAY_OPTIONS.find(d => d.value === day)?.label}`)).length === 0) {
                errors.push(t('surcharges.errors.not_covered', {day: DAY_OPTIONS.find(d => d.value === day)?.label, until: `${Math.floor(currentEnd/60)}:${(currentEnd%60).toString().padStart(2, '0')}`}));
            }
        }

        setValidationErrors(errors);
        return errors.length === 0;
    };

    const handleSave = async () => {
        if (!validateEntries()) {
            const firstError = validationErrors[0] || t('surcharges.validation_errors');
            toast.error(firstError, { duration: 5000 });

            const errorSection = document.querySelector('.bg-red-50');
            if (errorSection) {
                errorSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }

        setIsSaving(true);

        try {
            const payload = [
                ...entries.map(entry => ({
                    label: entry.label || '',
                    start_time: entry.start_time,
                    end_time: entry.end_time === '24:00' ? '00:00' : entry.end_time,
                    percentage_value: entry.percentage_value,
                    percentage_type: entry.percentage_type,
                    days: entry.days
                })),
                {
                    percentage_type: 'holidays',
                    label: t('surcharges.holidays'),
                    percentage_value: holidayPercentage
                },
                {
                    percentage_type: 'interrupted-shifts',
                    label: t('surcharges.interrupted_service'),
                    percentage_value: interruptedShiftPercentage
                }
            ];

            await updateBranchPercentages(branchId, payload);
            toast.success(t('surcharges.toast_saved'));
        } catch (error) {
            console.error('Error saving percentages', error);
            toast.error(t('surcharges.toast_save_error'));
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <BaseCard border={false}>
                <h2 className="text-lg text-gray-700 uppercase mb-4">{t('surcharges.title')}</h2>
                <div className="flex justify-center items-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                </div>
            </BaseCard>
        );
    }

    return (
        <BaseCard border={false}>
            <div className="flex justify-between items-center mb-4 mt-2">
                <div>
                    <h2 className="text-lg text-gray-700 uppercase">{t('surcharges.title')}</h2>
                    <p className="text-sm text-gray-500 mt-1">
                        {t('surcharges.description')}
                    </p>
                </div>
            </div>

            {validationErrors.length > 0 && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <h3 className="text-sm font-semibold text-red-800 mb-2">{t('surcharges.validation_errors')}:</h3>
                    <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
                        {validationErrors.map((error, idx) => (
                            <li key={idx}>{error}</li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="mb-6">
                <h3 className="text-md font-semibold text-gray-700 mb-3">{t('surcharges.fixed_surcharges')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg">
                        <label className="text-sm font-medium text-gray-700">{t('surcharges.holidays')}</label>
                        <div className="flex items-center">
                            <input
                                type="number"
                                value={holidayPercentage}
                                onChange={(e) => setHolidayPercentage(parseFloat(e.target.value) || 0)}
                                min="0"
                                max="1000"
                                step="0.01"
                                className="w-24 px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
                            />
                            <span className="ml-1 text-sm text-gray-600">%</span>
                        </div>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg">
                        <label className="text-sm font-medium text-gray-700">{t('surcharges.interrupted_service')}</label>
                        <div className="flex items-center">
                            <input
                                type="number"
                                value={interruptedShiftPercentage}
                                onChange={(e) => setInterruptedShiftPercentage(parseFloat(e.target.value) || 0)}
                                min="0"
                                max="1000"
                                step="0.01"
                                className="w-24 px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
                            />
                            <span className="ml-1 text-sm text-gray-600">%</span>
                        </div>
                    </div>
                </div>
            </div>

            <h3 className="text-md font-semibold text-gray-700 mb-3">{t('surcharges.dynamic_surcharges')}</h3>

            <div className="overflow-x-auto mb-4">
                <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{t('surcharges.label')}</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{t('surcharges.start_hour')}</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{t('surcharges.end_hour')}</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{t('surcharges.percentage')}</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{t('surcharges.type')}</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{t('surcharges.days')}</th>
                            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase w-20">{t('tables.actions')}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {entries.map((entry, index) => (
                            <tr key={entry.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                <td className="px-4 py-3">
                                    <input
                                        type="text"
                                        value={entry.label || ''}
                                        onChange={(e) => updateEntry(entry.id, 'label', e.target.value)}
                                        placeholder="Label"
                                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
                                    />
                                </td>
                                <td className="px-4 py-3">
                                    <input
                                        type="time"
                                        value={entry.start_time}
                                        onChange={(e) => updateEntry(entry.id, 'start_time', e.target.value)}
                                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
                                    />
                                </td>
                                <td className="px-4 py-3">
                                    <input
                                        type="time"
                                        value={entry.end_time === '24:00' ? '23:59' : entry.end_time}
                                        onChange={(e) => {
                                            let value = e.target.value;
                                            if (value === '23:59') value = '24:00';
                                            updateEntry(entry.id, 'end_time', value);
                                        }}
                                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
                                    />
                                </td>
                                <td className="px-4 py-3">
                                    <div className="flex items-center">
                                        <input
                                            type="number"
                                            value={entry.percentage_value}
                                            onChange={(e) => updateEntry(entry.id, 'percentage_value', parseFloat(e.target.value) || 0)}
                                            min="0"
                                            max="1000"
                                            step="0.01"
                                            className="w-24 px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
                                        />
                                        <span className="ml-1 text-sm text-gray-600">%</span>
                                    </div>
                                </td>
                                <td className="px-4 py-3">
                                    <select
                                        value={entry.percentage_type}
                                        onChange={(e) => updateEntry(entry.id, 'percentage_type', e.target.value)}
                                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
                                    >
                                        {TYPE_OPTIONS.map(type => (
                                            <option key={type.value} value={type.value}>{type.label}</option>
                                        ))}
                                    </select>
                                </td>
                                <td className="px-4 py-3">
                                    <div className="flex gap-1 flex-wrap">
                                        {DAY_OPTIONS.map(day => (
                                            <button
                                                key={day.value}
                                                type="button"
                                                onClick={() => toggleDay(entry.id, day.value)}
                                                className={`px-2 py-1 text-xs rounded-md transition-colors ${
                                                    entry.days && entry.days.includes(day.value)
                                                        ? 'bg-primary text-white'
                                                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                                }`}
                                            >
                                                {day.label}
                                            </button>
                                        ))}
                                    </div>
                                </td>
                                <td className="px-4 py-3 text-center">
                                    <button
                                        type="button"
                                        onClick={() => removeEntry(entry.id)}
                                        className="text-red-600 hover:text-red-800 transition-colors"
                                        title={t('general.actions.delete')}
                                    >
                                        <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="mb-6">
                <SecondaryButton onClick={addEntry}>
                    <FontAwesomeIcon icon={faPlus} className="mr-2" />
                    {t('surcharges.add_surcharge')}
                </SecondaryButton>
            </div>

            <div className="flex gap-2 justify-end pt-4 border-t border-gray-200">
                <SecondaryButton onClick={() => setValidationErrors([])}>
                    {t('surcharges.reset_validation')}
                </SecondaryButton>
                <PrimaryButton
                    onClick={handleSave}
                    disabled={isSaving}
                >
                    {isSaving ? t('surcharges.saving') : t('surcharges.save_button')}
                </PrimaryButton>
            </div>
        </BaseCard>
    );
};
